import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, CalendarDays, Clock, Users, Phone } from "lucide-react";
import { Overline, MaskLine, EASE } from "@/components/Motion";
import { IMAGES, CONTACT } from "@/data/content";

const API = process.env.REACT_APP_BACKEND_URL;

const TIME_SLOTS = [
  "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM", "2:00 PM", "2:30 PM", "3:00 PM",
  "6:00 PM", "6:30 PM", "7:00 PM", "7:30 PM", "8:00 PM", "8:30 PM", "9:00 PM", "9:30 PM", "10:00 PM",
];

const GUEST_OPTIONS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const inputCls = (hasError) =>
  `w-full rounded-2xl border bg-white/70 px-5 py-3.5 text-[15px] font-light outline-none transition-colors duration-300 placeholder:text-faded/60 focus:border-wine ${
    hasError ? "border-red-500" : "border-sand"
  }`;

const Err = ({ id, msg }) =>
  msg ? (
    <p data-testid={`reservation-error-${id}`} className="mt-1.5 text-xs text-red-600 tracking-wide">
      {msg}
    </p>
  ) : null;

const EMPTY = { name: "", email: "", phone: "", date: "", time: "", guests: "", requests: "" };

export default function ReservationsPage() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [confirmed, setConfirmed] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please tell us your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) er.email = "Please enter a valid email address.";
    if (form.phone.replace(/\D/g, "").length < 10) er.phone = "Please enter a valid 10-digit phone number.";
    if (!form.date) {
      er.date = "Please pick a date.";
    } else {
      const d = new Date(`${form.date}T00:00:00`);
      const today = new Date(`${todayStr()}T00:00:00`);
      if (d < today) er.date = "Please pick a date from today onwards.";
      else if (d.getDay() === 0) er.date = "We rest on Sundays — we're open Monday to Saturday.";
    }
    if (!form.time) er.time = "Please choose a time.";
    if (!form.guests) er.guests = "How many of you shall we expect?";
    return er;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = validate();
    if (Object.keys(er).length) {
      setErrors(er);
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      await axios.post(`${API}/api/reservations`, form);
      setConfirmed({ ...form });
    } catch (err) {
      setSubmitError("Something went wrong sending your request — please try again, or simply call us to book.");
    } finally {
      setSubmitting(false);
    }
  };

  const prettyDate = (dateStr) =>
    new Date(`${dateStr}T00:00:00`).toLocaleDateString("en-IN", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });

  return (
    <main className="pt-[72px]" data-testid="reservations-page">
      <section className="bg-wine text-cream py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <Overline className="text-brass">Reservations</Overline>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 max-w-4xl" data-testid="reservations-page-title">
            <MaskLine>Your Table</MaskLine>
            <MaskLine delay={0.15}>
              <span className="italic text-brass">Awaits</span>
            </MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
            className="mt-6 max-w-xl text-cream/80 text-base sm:text-lg font-light leading-relaxed"
          >
            Open Monday to Saturday. Tell us when, and we'll set the candles.
          </motion.p>
          <motion.a
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
            href={`tel:${CONTACT.phone.replace(/\s/g, "")}`}
            data-testid="reservations-call-btn"
            className="mt-8 inline-flex items-center gap-3 rounded-full border border-cream/30 px-7 py-3.5 text-sm tracking-[0.14em] uppercase text-cream hover:bg-cream hover:text-wine transition-colors duration-300"
          >
            <Phone size={15} strokeWidth={1.5} />
            Or call to book — {CONTACT.phone}
          </motion.a>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid gap-14 lg:grid-cols-[1fr_1.1fr] items-start">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: EASE }}
            className="relative overflow-hidden rounded-[2rem] hidden lg:block"
          >
            <img
              src={IMAGES.reserve}
              alt="A candlelit table for two with mocktails at Forco Amore"
              className="w-full aspect-[4/5] object-cover"
              data-testid="reservations-side-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
            <div className="absolute bottom-0 p-10 text-cream">
              <p className="font-serif italic text-2xl leading-snug max-w-sm">
                "Where every meal becomes a love story."
              </p>
              <p className="mt-4 text-xs tracking-[0.3em] uppercase text-cream/70">
                Monday – Saturday · {CONTACT.address.split(",")[2]?.trim() || "Gurugram"}
              </p>
            </div>
          </motion.div>

          <AnimatePresence mode="wait">
            {!confirmed ? (
              <motion.form
                key="form"
                noValidate
                onSubmit={onSubmit}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="rounded-[2rem] border border-sand bg-shell p-8 sm:p-12"
                data-testid="reservation-form"
              >
                <h2 className="font-serif font-light text-3xl sm:text-4xl tracking-tight">
                  Book Your <span className="italic text-wine">Evening</span>
                </h2>
                <p className="mt-3 text-faded text-sm font-light">
                  For parties of 13 or more, or full-event catering, call us at {CONTACT.phone}.
                </p>

                <div className="mt-10 grid gap-6 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="mb-2 block text-[11px] tracking-[0.25em] uppercase text-faded">Your Name *</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={set("name")}
                      placeholder="e.g. Aarav Sharma"
                      className={inputCls(errors.name)}
                      data-testid="reservation-name-input"
                    />
                    <Err id="name" msg={errors.name} />
                  </div>

                  <div>
                    <label className="mb-2 block text-[11px] tracking-[0.25em] uppercase text-faded">Email *</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="you@example.com"
                      className={inputCls(errors.email)}
                      data-testid="reservation-email-input"
                    />
                    <Err id="email" msg={errors.email} />
                  </div>

                  <div>
                    <label className="mb-2 block text-[11px] tracking-[0.25em] uppercase text-faded">Phone *</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      placeholder="+91 98XXXXXXXX"
                      className={inputCls(errors.phone)}
                      data-testid="reservation-phone-input"
                    />
                    <Err id="phone" msg={errors.phone} />
                  </div>

                  <div>
                    <label className="mb-2 block text-[11px] tracking-[0.25em] uppercase text-faded">Date *</label>
                    <input
                      type="date"
                      min={todayStr()}
                      value={form.date}
                      onChange={set("date")}
                      className={inputCls(errors.date)}
                      data-testid="reservation-date-input"
                    />
                    <Err id="date" msg={errors.date} />
                  </div>

                  <div>
                    <label className="mb-2 block text-[11px] tracking-[0.25em] uppercase text-faded">Time *</label>
                    <select
                      value={form.time}
                      onChange={set("time")}
                      className={inputCls(errors.time)}
                      data-testid="reservation-time-select"
                    >
                      <option value="">Choose a time</option>
                      {TIME_SLOTS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                    <Err id="time" msg={errors.time} />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="mb-2 block text-[11px] tracking-[0.25em] uppercase text-faded">Guests *</label>
                    <select
                      value={form.guests}
                      onChange={set("guests")}
                      className={inputCls(errors.guests)}
                      data-testid="reservation-guests-select"
                    >
                      <option value="">How many guests?</option>
                      {GUEST_OPTIONS.map((g) => (
                        <option key={g} value={g}>{g} {g === "1" ? "guest" : "guests"}</option>
                      ))}
                    </select>
                    <Err id="guests" msg={errors.guests} />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="mb-2 block text-[11px] tracking-[0.25em] uppercase text-faded">Special Requests (optional)</label>
                    <textarea
                      rows={3}
                      value={form.requests}
                      onChange={set("requests")}
                      placeholder="Anniversary, birthday cake, window table, allergies…"
                      className={`${inputCls(false)} resize-none`}
                      data-testid="reservation-requests-input"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="reservation-submit-btn"
                  className="mt-10 w-full rounded-full bg-wine px-12 py-5 text-sm tracking-[0.16em] uppercase text-cream hover:bg-winedark hover:scale-[1.02] active:scale-95 transition-all duration-300 shadow-[0_20px_50px_-15px_rgba(114,47,55,0.5)] disabled:opacity-60 disabled:pointer-events-none"
                >
                  {submitting ? "Setting your table…" : "Reserve My Table"}
                </button>
                {submitError && (
                  <p className="mt-4 text-center text-sm text-red-600" data-testid="reservation-submit-error">
                    {submitError}{" "}
                    <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="underline">{CONTACT.phone}</a>
                  </p>
                )}
                <p className="mt-5 text-center text-xs text-faded tracking-wide">
                  Closed on Sundays · Prefer to talk? Call{" "}
                  <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="reservation-form-call-link" className="text-wine underline">{CONTACT.phone}</a>{" "}
                  and we'll book you in. Our team confirms every online request over phone or email.
                </p>
              </motion.form>
            ) : (
              <motion.div
                key="thankyou"
                initial={{ opacity: 0, scale: 0.96, y: 30 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.8, ease: EASE }}
                className="rounded-[2rem] border border-sand bg-shell p-8 sm:p-12 text-center"
                data-testid="reservation-thankyou"
              >
                <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-wine text-cream">
                  <Heart size={30} strokeWidth={1.4} className="fill-current" />
                </span>
                <h2 className="mt-8 font-serif font-light text-3xl sm:text-4xl tracking-tight" data-testid="reservation-thankyou-title">
                  Grazie, <span className="italic text-wine">{confirmed.name.split(" ")[0]}!</span>
                </h2>
                <p className="mt-4 text-faded text-base font-light leading-relaxed max-w-md mx-auto">
                  Your table request is in — the candles are as good as lit. Our team will reach out
                  on {confirmed.phone} to warmly confirm your reservation.
                </p>

                <div className="mt-10 grid gap-4 sm:grid-cols-3 text-left" data-testid="reservation-recap">
                  <div className="rounded-2xl border border-sand bg-cream p-5">
                    <CalendarDays size={18} className="text-wine" strokeWidth={1.5} />
                    <p className="mt-3 text-[10px] tracking-[0.25em] uppercase text-faded">Date</p>
                    <p className="mt-1 font-serif text-lg leading-tight">{prettyDate(confirmed.date)}</p>
                  </div>
                  <div className="rounded-2xl border border-sand bg-cream p-5">
                    <Clock size={18} className="text-wine" strokeWidth={1.5} />
                    <p className="mt-3 text-[10px] tracking-[0.25em] uppercase text-faded">Time</p>
                    <p className="mt-1 font-serif text-lg leading-tight">{confirmed.time}</p>
                  </div>
                  <div className="rounded-2xl border border-sand bg-cream p-5">
                    <Users size={18} className="text-wine" strokeWidth={1.5} />
                    <p className="mt-3 text-[10px] tracking-[0.25em] uppercase text-faded">Guests</p>
                    <p className="mt-1 font-serif text-lg leading-tight">{confirmed.guests}</p>
                  </div>
                </div>

                <div className="mt-10 flex flex-wrap justify-center gap-4">
                  <Link
                    to="/menu"
                    data-testid="thankyou-menu-btn"
                    className="rounded-full bg-wine px-9 py-4 text-sm tracking-[0.14em] uppercase text-cream hover:bg-winedark transition-colors duration-300"
                  >
                    Browse the Menu
                  </Link>
                  <button
                    onClick={() => {
                      setForm(EMPTY);
                      setConfirmed(null);
                    }}
                    data-testid="thankyou-new-reservation-btn"
                    className="rounded-full border border-wine px-9 py-4 text-sm tracking-[0.14em] uppercase text-wine hover:bg-wine hover:text-cream transition-colors duration-300"
                  >
                    Make Another Booking
                  </button>
                </div>
                <p className="mt-8 text-xs text-faded tracking-wide italic">
                  A presto — we can't wait to host you. ♥
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </main>
  );
}
